import React, { memo, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { CalendarOptions } from "@fullcalendar/common";
import enAULocale from "@fullcalendar/core/locales/en-au";
import enGBLocale from "@fullcalendar/core/locales/en-gb";
import enNZLocale from "@fullcalendar/core/locales/en-nz";
import hiLocale from "@fullcalendar/core/locales/hi";
import ruLocale from "@fullcalendar/core/locales/ru";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useIntl } from "react-intl";

import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { getLocale } from "@app/store/locale";
import { RootState } from "@app/store/rootReducer";

import styles from "./BaseCalendar.module.scss";

export const VIEW_MODE_TO_FULL_CALENDAR_VIEW_NAME: Record<CalendarViewMode, string> = {
  [CalendarViewMode.DAY]: "timeGridDay",
  [CalendarViewMode.MONTH]: "dayGridMonth",
  [CalendarViewMode.WEEK]: "timeGridWeek",
};

export const fullCalendarViewNameToViewMode: Record<string, CalendarViewMode> = {
  dayGridMonth: CalendarViewMode.MONTH,
  timeGridDay: CalendarViewMode.DAY,
  timeGridWeek: CalendarViewMode.WEEK,
};

interface Props
  extends Omit<
    CalendarOptions,
    "dateClick" | "eventClick" | "eventDrop" | "eventResize" | "select"
  > {
  locale: string;
  onContainerRef?: (el: HTMLDivElement) => void;
  onDateClick?: CalendarOptions["dateClick"];
  onDayCellDidMount?: () => void;
  onEventClick?: CalendarOptions["eventClick"];
  onEventDrop?: CalendarOptions["eventDrop"];
  onEventResize?: CalendarOptions["eventResize"];
  onSelect?: CalendarOptions["select"];
  timeFrameStartDate: string;
  viewMode?: CalendarViewMode;
  viewModeToFullCalendarViewName?: Record<CalendarViewMode, string>;
}

const BaseCalendar: React.FC<Props> = ({
  locale,
  onContainerRef,
  onDateClick,
  onDayCellDidMount,
  onEventClick,
  onEventDrop,
  onEventResize,
  onSelect,
  plugins,
  timeFrameStartDate,
  viewMode = CalendarViewMode.MONTH,
  viewModeToFullCalendarViewName = VIEW_MODE_TO_FULL_CALENDAR_VIEW_NAME,
  ...props
}) => {
  const intl = useIntl();

  const { calendarApi, setCalendarApi } = useCalendarView();
  const setCalendarRef = useCallback(
    (element) => {
      setCalendarApi(element?.getApi());
    },
    [setCalendarApi]
  );

  useEffect(() => {
    calendarApi?.gotoDate(timeFrameStartDate);
    calendarApi?.changeView(viewModeToFullCalendarViewName[viewMode]);
  }, [calendarApi, timeFrameStartDate, viewMode, viewModeToFullCalendarViewName]);

  const handleMoreLinkContent = useCallback(
    ({ num }: { num: number }) =>
      intl.formatMessage(
        {
          id: "baseCalendar.moreLink",
          defaultMessage: "+{num} more",
          description: "More link text in Calendars",
        },
        { num }
      ),
    [intl]
  );

  return (
    <div className={styles.container} ref={onContainerRef}>
      <FullCalendar
        allDayClassNames={styles.allDayText}
        allDayContent={intl.formatMessage({
          id: "baseCalendar.allDay",
          defaultMessage: "All Day",
          description: "All Day text in Calendars",
        })}
        contentHeight={"100%"}
        dateClick={onDateClick}
        dayCellDidMount={onDayCellDidMount}
        eventClick={onEventClick}
        eventDrop={onEventDrop}
        eventResize={onEventResize}
        height={"auto"}
        initialView={viewModeToFullCalendarViewName[viewMode]}
        locale={locale}
        locales={[hiLocale, ruLocale, enAULocale, enGBLocale, enNZLocale]}
        moreLinkContent={handleMoreLinkContent}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, ...(plugins ?? [])]}
        ref={setCalendarRef}
        select={onSelect}
        slotLabelClassNames={styles.slotLabelText}
        {...props}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  locale: getLocale(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(memo(BaseCalendar));
